<template>
  <v-container id="jumpEventResult">
    <AppCompetitionResultsFilter
      v-model="filters"
      :filter-name="`horse-competitions-${horse.id}`"
    />
    <AppActionsRow>
      <v-btn
        v-if="
          $auth.checkUserCanPerformMutationsInScopeAny(
            'extResApplicationCreate'
          )
        "
        variant="text"
        size="large"
        class="normal-text"
        color="primary"
        :to="{ name: 'NewExternalResultForm' }"
      >
        <v-icon start> $iconPage </v-icon>
        {{ $t("common:reportResults.button") }}
      </v-btn>
      <AppExportFile :prepare-function="getResults">
        <template #button="btnProps">
          <v-btn
            color="primary"
            size="large"
            variant="text"
            class="normal-text"
            v-bind="btnProps"
            :disabled="results.length === 0"
          >
            <b>
              <v-icon start>$iconDownload</v-icon>
              <slot>{{
                $t("common:button.downloadCsv", "Download as csv")
              }}</slot>
            </b>
          </v-btn>
        </template>
      </AppExportFile>
      <AppPrintEntityCompetitionResultsTemplate
        entity-type="horseId"
        :entity-object="horse"
        :filters="filters"
        class="normal-text"
      />
      <v-btn
        class="normal-text"
        variant="text"
        size="large"
        color="primary"
        :aria-label="$t('common:expandButton.alt')"
        data-test="expander"
        @click.prevent="rowExpand()"
      >
        <div>
          <v-icon
            start
            :style="expandsBoolean ? 'transform: rotate(180deg)' : ''"
          >
            $iconCaretDown
          </v-icon>
          <span class="text-primary">{{
            $t("common:results.showDetails.button")
          }}</span>
        </div>
      </v-btn>
    </AppActionsRow>
    <v-card color="primary" class="fipa--header" tile />
    <v-data-table
      ref="resultsTable"
      v-model:expanded="expanded"
      :items="results"
      :headers="headers"
      :items-per-page="-1"
      hide-default-footer
      :loading="$apollo.queries.results.loading"
      :hide-default-header="$vuetify.display.smAndDown"
      class="no-hover entsys--table"
      item-value="id"
      disable-sort
    >
      <template #item="{ item, toggleExpand, internalItem, isExpanded }">
        <template v-if="$vuetify.display.mdAndUp">
          <tr class="entsys--next-results-edit keep-top">
            <td>
              <div class="my-2">
                {{ $dayjs.date(item.competition.startDate) }}
              </div>
            </td>
            <td>
              <div class="my-2">
                <AppShowEvent
                  :event="item.competition.event"
                  event-page="EventResultsPage"
                  :additional-params="{
                    resultsId: item.id,
                    competitionClass: item.competition.code
                  }"
                />
              </div>
            </td>
            <td>
              <div class="my-2">
                <AppShowDisciplineName
                  :name="item.competition.event.discipline.name"
                  chip
                />
              </div>
            </td>

            <td>
              <div class="my-2">
                <AppShowPersonNameWithIdentifiers
                  :person="item.registration.athletes[0]"
                />
              </div>
            </td>

            <td class="text-center">
              <div class="my-2">
                <div class="text-secondary">
                  {{ get(item.competition, "competitionLevel.name", "") }}
                </div>

                <AppShowDifficultyLevels
                  :value="item.competition.difficultyLevel"
                >
                  <template #append>
                    <span v-if="item.sectionId">
                      (<AppShowSection :section="item.section" />)
                    </span>
                  </template>
                </AppShowDifficultyLevels>

                <div>{{ get(item, "competitionRule.name", "") }}</div>
              </div>
            </td>
            <td class="text-center">
              <div class="my-2">
                <AppShowResultPreview
                  v-if="
                    !item.outsideOfCompetition ||
                    item.competition.event.discipline.codename == 'endurance'
                  "
                  :result="item"
                />
              </div>
            </td>
            <td class="text-center">
              <div class="my-2">
                <template v-if="item.outsideOfCompetition">
                  {{
                    $t(
                      "events:results.position.outsideOfCompetition.text",
                      "HC"
                    )
                  }}
                </template>
                <template v-else-if="item.teamResult">
                  {{ item.teamResult.rank }}
                  {{ $t("common:common.results.team.text") }}
                  <template v-if="!ignoreIndividualRank(item)">
                    / {{ item.rank }}
                    {{ $t("common:common.results.individualAbrv.text") }}
                  </template>
                </template>
                <template v-else>
                  <AppShowRankAndStarts :item="item" />
                </template>
              </div>
            </td>

            <td class="text-center currency-display">
              <div v-if="item.prizeMoneyCents" class="my-2">
                {{ masterCurrency(item.prizeMoneyCents, { onlySymbol: true }) }}
              </div>
              <div v-else-if="item.isPlaced" class="my-2">
                <v-icon>$iconRosette</v-icon>
              </div>
            </td>
            <td>
              <div class="my-2">
                <v-btn-expand
                  :model-value="isExpanded(internalItem)"
                  @click="toggleExpand(internalItem)"
                >
                </v-btn-expand>
              </div>
            </td>
          </tr>
        </template>
        <template v-else>
          <tr>
            <td style="font-size: 12px !important">
              <AppShowEvent
                :event="item.competition.event"
                event-page="EventResultsPage"
                :additional-params="{
                  resultsId: item.id,
                  competitionClass: item.competition.code
                }"
              />
              <AppShowDisciplineName
                :name="item.competition.event.discipline.name"
                chip
              />
            </td>
            <td class="text-center">
              <div class="my-2">
                <template v-if="item.outsideOfCompetition">
                  {{
                    $t(
                      "events:results.position.outsideOfCompetition.text",
                      "HC"
                    )
                  }}
                </template>
                <template v-else-if="item.teamResult">
                  {{ item.teamResult.rank }}
                  {{ $t("common:common.results.team.text") }}
                  <template v-if="!ignoreIndividualRank(item)">
                    / {{ item.rank }}
                    {{ $t("common:common.results.individualAbrv.text") }}
                  </template>
                </template>
                <template v-else>
                  <AppShowRankAndStarts :item="item" />
                </template>
              </div>
            </td>
            <td class="text-right" style="width: 0">
              <div>
                <v-btn-expand
                  :model-value="isExpanded(internalItem)"
                  @click="toggleExpand(internalItem)"
                >
                </v-btn-expand>
              </div>
            </td>
          </tr>
        </template>
      </template>
      <template #expanded-row="{ item }">
        <AppCompetitionResultExpanded
          :item="item"
          :horse-id="horse.id"
          profile="horse"
        />
      </template>
    </v-data-table>

    <div class="text-right">
      <AppLoadMoreBtn
        :has-more-results="hasMoreResults"
        :loading="$apollo.queries.results.loading"
        @load-more="loadMore"
      />
    </div>
  </v-container>
</template>
<script>
import { defineComponent } from "vue";
import gql from "fraql";
import { useDisciplineHelper } from "@composables/masterdata/competitions/disciplines";
import { useCompetitionRuleHelper } from "@composables/masterdata/competitions/competitionRules";
import { useCurrency } from "@composables/masterdata/config";
import { get } from "lodash-es";
import AppLoadMoreBtn from "@elements/AppLoadMoreBtn.vue";

import { useCompetitionResultsExport } from "@composables/exports/competition_results";
import AppCompetitionResultsFilter, {
  QUERY_FILTERS,
  QUERY_FILTER_VALUES
} from "@elements/AppCompetitionResultsFilter.vue";

import AppShowEvent from "@AppShows/event/AppShowEvent.vue";
import AppShowPersonNameWithIdentifiers from "@AppShows/person/AppShowPersonNameWithIdentifiers.vue";
import AppExportFile from "@elements/AppExportFile.vue";

import AppShowDisciplineName from "@AppShows/AppShowDisciplineName.vue";
import AppShowResultPreview from "@AppShows/AppShowResultPreview.vue";
import AppShowDifficultyLevels from "@AppShows/AppShowDifficultyLevels.vue";
import AppCompetitionResultExpanded from "@elements/AppCompetitionResultExpanded.vue";
import AppShowRankAndStarts from "@AppShows/AppShowRankAndStarts.vue";

import AppPrintEntityCompetitionResultsTemplate from "@elements/prints/AppPrintEntityCompetitionResultsTemplate.vue";
import { AppPrintEntityCompetitionResultsTemplateFragments } from "@elements/prints/AppPrintEntityCompetitionResultsTemplateFragments";
import AppShowSection from "@AppShows/event/AppShowSection.vue";
import AppActionsRow from "@elements/AppActionsRow.vue";

export default defineComponent({
  name: "HorseCompetitionResults",
  components: {
    AppShowEvent,
    AppShowDisciplineName,
    AppShowResultPreview,
    AppLoadMoreBtn,
    AppCompetitionResultsFilter,
    AppCompetitionResultExpanded,
    AppShowDifficultyLevels,
    AppExportFile,
    AppShowRankAndStarts,
    AppPrintEntityCompetitionResultsTemplate,
    AppShowPersonNameWithIdentifiers,
    AppShowSection,
    AppActionsRow
  },
  props: { horse: { type: Object, required: true } },
  setup() {
    const { getCompetitionResults } = useCompetitionResultsExport();

    return {
      ...useCurrency(),
      ...useDisciplineHelper(),
      ...useCompetitionRuleHelper(),
      getCompetitionResults
    };
  },
  data() {
    return {
      results: [],
      hasMoreResults: true,
      expandsBoolean: false,
      expanded: [],
      filters: {
        cupIds: [],
        satisfiesQualification: null,
        hasPoints: null,
        disciplineIds: [],
        isPlaced: null,
        inTeamCompetition: null,
        competitionStartDateFrom: null,
        competitionStartDateUntil: null
      }
    };
  },
  computed: {
    headers() {
      return [
        {
          title: this.$t(
            "calendar:tableview.Date.column",
            "Date",
            "Date column name in calendar table view"
          ),
          align: "left",
          value: null
        },
        {
          title: this.$t(
            "calendar:tableview.EventOrganiser.column",
            "Event / Organiser",
            "Event and organiser column name in calendar table view"
          ),
          value: null
        },
        {
          title: this.$t(
            "calendar:tableview.Discipline.column",
            "Discipline",
            "Discipline column name in calendar table view"
          ),
          value: null
        },
        {
          title: this.$t(
            "events:common.results.athlete.column",
            "Athlete",
            "Label for athlete column in results"
          ),
          value: null
        },

        {
          title: this.$t(
            "people:PersonProfile.CompetitionResultsSection.CompetitionLevel.label",
            "Competition level"
          ),
          align: "center",
          value: null
        },
        {
          title: this.$t(
            "events:common.result.column",
            "Result",
            "Label for result column in results"
          ),
          align: "center",
          value: null
        },
        {
          title: this.$t(
            "events:common.rank.column",
            "Rank",
            "Label for rank column in results"
          ),
          align: "center",
          value: null
        },

        {
          title: this.$t(
            "people:PersonProfile.CompetitionResultsSection.Award.label",
            "Award"
          ),
          value: null,
          align: "center"
        },
        { title: "", value: null }
      ];
    }
  },
  watch: {
    filters: {
      deep: true,
      handler() {
        this.expanded = [];
        this.expandsBoolean = false;
      }
    }
  },
  methods: {
    get,
    ignoreIndividualRank(item) {
      return ["trec", "mountedgames"].includes(
        item.competition.event.discipline.name
      );
    },
    async getResults() {
      const { results, keyMap } = await this.getCompetitionResults(
        "horseId",
        this.horse.id,
        this.filters
      );

      return { keyMap, items: results, filename: "horse-results-export" };
    },
    rowExpand() {
      this.expandsBoolean = !this.expandsBoolean;
      if (this.expandsBoolean) {
        this.expanded = this.results.map(x => x.id);
      } else {
        this.expanded = [];
      }
    },
    loadMore() {
      // Fetch more data and transform the original result
      this.$apollo.queries.results
        .fetchMore({
          // New variables
          variables: { offset: this.results.length }
        })
        .then(({ data: { results } }) => {
          this.hasMoreResults = results.length >= 25;
        });
    }
  },
  apollo: {
    results: {
      query: gql`
        query horseResults ( 
            $horseId: ID! 
            $offset: Int
            $limit: Int 
            $sortBy: String
            $sortDesc: Boolean
            ${QUERY_FILTERS}
          ) {
          results (
            horseId: $horseId
            offset: $offset
            limit: $limit
            sortBy: $sortBy
            sortDesc: $sortDesc
            ${QUERY_FILTER_VALUES}
          ) {
            id
            rank
            isPlaced
            prizeMoneyCents
            outsideOfCompetition
            sectionId
            section {
              id
              ${AppShowSection.fragments.sectionFields}
            }
            ${AppCompetitionResultExpanded.fragments.resultFields}
            competitionRuleId
            competitionRule @client {
              id
              name
            }
            ${AppShowRankAndStarts.fragments.rankStarts}
            ${AppShowResultPreview.fragments.result}
            points {
              id
              ${AppCompetitionResultExpanded.fragments.pointFields}
              chartRule {
                id
                chart {
                  id
                  name
                  ${AppCompetitionResultExpanded.fragments.horseChart}
                }
              }
            }

            teamResult {
              id
              rank
            }

            registration {
              id
              teamName
              groomNames
              carriageNumber
              drivingCarriageWidth
              ${AppCompetitionResultExpanded.fragments.registrationFields}
              vaultingLungerPerson {
                id
                ${AppShowPersonNameWithIdentifiers.fragments.person}
              }
              athletes {
                id
                ${AppShowPersonNameWithIdentifiers.fragments.person}
              }
            }
            competition {
              id
              code
              name
              startDate
              description
              ${AppCompetitionResultExpanded.fragments.competition}
              competitionLevel {
                id
                name
              }
              eventcode {
                id
                name
              }
             
              event {
                id
                ${AppShowDisciplineName.fragments.eventDiscipline}
                ${AppShowEvent.fragments.event}
                countryId
                organiser {
                  id
                  name
                }
                disciplineId
                discipline @client {
                  id
                  name
                  codename
                }
                regionId
                region @client {
                  id
                  name
                }
                place
              }
            }
          }
        }
      `,
      variables() {
        return {
          horseId: this.horse.id,
          offset: 0,
          limit: 25,
          sortBy: "competitionStartDate",
          sortDesc: true,
          ...this.filters
        };
      },
      result({ loading, data }) {
        if (loading || !data) return;
        this.hasMoreResults = data.results.length >= 25;
      },
      fetchPolicy: "cache-and-network",
      nextFetchPolicy: "cache-first"
    }
  },
  fragments: {
    horseFields: gql`
      fragment _ on Horse {
        ${AppPrintEntityCompetitionResultsTemplateFragments.horseFields}
      }
    `
  }
});
</script>
<style scoped>
.competition-results {
  display: flex;
  justify-content: center;
  flex-direction: column;
  border-radius: 2px;
  border: solid 1px #9b9b9b;
  box-shadow: 0 0px 0px 2px rgba(0, 0, 0, 0.2);
}
.award-prize {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}
.expanded-title {
  color: rgb(var(--v-theme-muted));
  font-size: 12px;
}
</style>
